const EMPRESA_ACTIVA_STORAGE_KEY = "empresaActivaId";
const UNIDAD_ACTIVA_STORAGE_KEY = "unidadNegocioActiva";
const EVENTO_EMPRESA_ACTIVA = "empresa-activa-cambiada";
const EVENTO_UNIDAD_ACTIVA = "unidad-negocio-activa-cambiada";

type EmpresaSesion = {
  id?: number;
  empresaId?: number;
  nombre?: string | null;
  empresaNombre?: string | null;
  empresa?: { id?: number; nombre?: string | null };
};

export type EmpresaDisponible = {
  id: number;
  nombre: string;
};

export type UnidadNegocioDisponible = {
  id: number;
  nombre: string;
  codigo?: string | null;
};

const leerAuthUser = (): any | null => {
  if (typeof window === "undefined") return null;
  const storageValue =
    sessionStorage.getItem("authUser") ?? localStorage.getItem("authUser");
  if (!storageValue) return null;
  try {
    return JSON.parse(storageValue);
  } catch {
    return null;
  }
};

const extraerEmpresasDesdeSesion = (usuario: any): EmpresaDisponible[] => {
  if (!usuario) return [];
  const origen: EmpresaSesion[] = Array.isArray(usuario?.empresas)
    ? usuario.empresas
    : Array.isArray(usuario?.empresasAsignadas)
      ? usuario.empresasAsignadas
      : [];
  const empresas = origen
    .map((empresa) => {
      const id =
        typeof empresa?.id === "number"
          ? empresa.id
          : typeof empresa?.empresaId === "number"
            ? empresa.empresaId
            : typeof empresa?.empresa?.id === "number"
              ? empresa.empresa.id
              : null;
      if (!id || !Number.isFinite(id)) {
        return null;
      }
      const nombre =
        empresa?.nombre ??
        empresa?.empresaNombre ??
        empresa?.empresa?.nombre ??
        `Empresa ${id}`;
      return { id, nombre };
    })
    .filter(Boolean) as EmpresaDisponible[];

  if (empresas.length > 0) {
    return empresas;
  }

  const fallbackId =
    typeof usuario?.empresaId === "number" ? usuario.empresaId : null;
  const fallbackNombre = usuario?.empresaNombre ?? `Empresa ${fallbackId ?? ""}`;
  return fallbackId ? [{ id: fallbackId, nombre: fallbackNombre }] : [];
};

const persistirEmpresaActivaId = (id: number | null) => {
  if (typeof window === "undefined") return;
  if (id === null) {
    window.localStorage.removeItem(EMPRESA_ACTIVA_STORAGE_KEY);
  } else {
    window.localStorage.setItem(EMPRESA_ACTIVA_STORAGE_KEY, String(id));
  }
};

const notificarCambioEmpresa = (id: number | null) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENTO_EMPRESA_ACTIVA, { detail: id }));
};

export const guardarEmpresaActivaId = (id: number | null) => {
  persistirEmpresaActivaId(id);
  notificarCambioEmpresa(id);
};

export const obtenerEmpresasDisponibles = (): EmpresaDisponible[] => {
  const auth = leerAuthUser();
  return extraerEmpresasDesdeSesion(auth?.usuario ?? auth);
};

export const obtenerEmpresaActivaId = (): number | null => {
  if (typeof window === "undefined") return null;
  const empresas = obtenerEmpresasDisponibles();
  const storedRaw = window.localStorage.getItem(EMPRESA_ACTIVA_STORAGE_KEY);
  const storedId =
    storedRaw && Number.isFinite(Number(storedRaw)) ? Number(storedRaw) : null;

  if (storedId !== null && empresas.some((empresa) => empresa.id === storedId)) {
    return storedId;
  }

  const fallback = empresas[0]?.id ?? null;
  persistirEmpresaActivaId(fallback);
  return fallback;
};

export const suscribirseCambioEmpresaActiva = (
  handler: (id: number | null) => void,
) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const listener = (event: Event) => {
    const detalle = (event as CustomEvent<number | null>).detail ?? null;
    handler(detalle);
  };
  window.addEventListener(EVENTO_EMPRESA_ACTIVA, listener as EventListener);
  return () =>
    window.removeEventListener(EVENTO_EMPRESA_ACTIVA, listener as EventListener);
};

type UnidadActivaMapa = Record<string, number | null>;

const leerMapaUnidades = (): UnidadActivaMapa => {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(UNIDAD_ACTIVA_STORAGE_KEY);
  if (!raw) return {};
  try {
    const data = JSON.parse(raw);
    if (typeof data === "object" && data !== null) {
      return data as UnidadActivaMapa;
    }
  } catch {
    return {};
  }
  return {};
};

const escribirMapaUnidades = (mapa: UnidadActivaMapa) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(UNIDAD_ACTIVA_STORAGE_KEY, JSON.stringify(mapa));
};

export const obtenerUnidadNegocioActivaId = (
  empresaId?: number | null,
): number | null => {
  if (!empresaId || typeof window === "undefined") {
    return null;
  }
  const mapa = leerMapaUnidades();
  const valor = mapa[String(empresaId)];
  return typeof valor === "number" && Number.isFinite(valor)
    ? valor
    : null;
};

export const guardarUnidadNegocioActivaId = (
  empresaId: number,
  unidadId: number | null,
) => {
  if (typeof window === "undefined") return;
  const mapa = leerMapaUnidades();
  mapa[String(empresaId)] = unidadId;
  escribirMapaUnidades(mapa);
  window.dispatchEvent(
    new CustomEvent(EVENTO_UNIDAD_ACTIVA, {
      detail: { empresaId, unidadId },
    }),
  );
};

export const suscribirseCambioUnidadActiva = (
  handler: (payload: { empresaId: number; unidadId: number | null }) => void,
) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const listener = (event: Event) => {
    const detalle = (event as CustomEvent<{ empresaId: number; unidadId: number | null }>)
      .detail;
    if (detalle && typeof detalle.empresaId === "number") {
      handler(detalle);
    }
  };
  window.addEventListener(EVENTO_UNIDAD_ACTIVA, listener as EventListener);
  return () =>
    window.removeEventListener(
      EVENTO_UNIDAD_ACTIVA,
      listener as EventListener,
    );
};
